import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import bgIm from "assets/images/t2CCNu53AN.gif";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Box } from "@mui/material";

// Common Components
import pxToRem from "assets/theme/functions/pxToRem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Utils
import Constants from "utils/Constants";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DEFAULT_COLORS = ["#5A8DEE", "#39DA8A", "#FF5B5C", "#FFA117", "#00CFDD", "#826AF9"];

const isValidColor = (color) => {
  if (!color || typeof color !== "string") return false;

  const tempElement = document.createElement("div");
  tempElement.style.color = color;
  return tempElement.style.color !== "";
};

const DATASET_DEFAULTS = {
  borderRadius: 6,
  maxBarThickness: 40,
};

const MINUTES_PER_HOUR = 60;
const HOURS_AXIS_LABEL = "Hours per Scope";

const convertMinutesToHours = (minutes) => {
  if (!Number.isFinite(minutes)) return 0;

  const safeMinutes = Math.max(0, minutes);
  const hours = safeMinutes / MINUTES_PER_HOUR;

  return Number(hours.toFixed(2));
};

const parseDurationStringToMinutes = (value) => {
  if (!value || typeof value !== "string") return 0;

  const hoursMatch = value.match(/([\d.]+)\s*h/i);
  const minutesMatch = value.match(/([\d.]+)\s*m/i);

  let totalMinutes = 0;

  if (hoursMatch) {
    const hoursValue = Number.parseFloat(hoursMatch[1]);
    if (Number.isFinite(hoursValue)) {
      totalMinutes += hoursValue * MINUTES_PER_HOUR;
    }
  }

  if (minutesMatch) {
    const minutesValue = Number.parseFloat(minutesMatch[1]);
    if (Number.isFinite(minutesValue)) {
      totalMinutes += minutesValue;
    }
  }

  return Math.max(0, Math.round(totalMinutes));
};

const formatMinutesToDuration = (minutesValue) => {
  if (!Number.isFinite(minutesValue)) return "";

  const safeMinutes = Math.max(0, Math.round(minutesValue));
  const hours = Math.floor(safeMinutes / MINUTES_PER_HOUR);
  const minutes = safeMinutes % MINUTES_PER_HOUR;

  return `${hours}h ${minutes}min`;
};

const BASE_CHART_OPTIONS = {
  responsive: true,
  interaction: {
    mode: "index",
    intersect: false,
  },
  layout: {
    padding: {
      top: 30, // add extra space for labels above bars
    },
  },
  plugins: {
    title: { display: false },
    tooltip: {
      padding: 12,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      displayColors: false,
    },
    datalabels: {
      display: false, // 🚫 hide all data labels inside bars
    },
  },
};

function BarChart({
  labels,
  datasets,
  options,
  title,
  description,
  height,
  displayLegend,
  showGrid,
  yBeginAtZero,
  maintainAspectRatio,
  isStacked,
  data,
  labelKey,
  valueKey,
  datasetLabel,
  datasetOptions,
  colorCodeKey,
  showAverageLine,
  averageLineColor,
  averageLineLabel,
  durationKey,
  loading,
}) {
  const parseNumericValue = useCallback((value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }, []);

  const getBarColors = useCallback(
    (dataItems) =>
      dataItems.map((item, index) => {
        const color = item?.[colorCodeKey];
        if (color && isValidColor(color)) {
          return color;
        }
        return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
      }),
    [colorCodeKey]
  );

  const calculateAverage = useCallback((values) => {
    if (!values || values.length === 0) return 0;
    const validValues = values.filter((val) => typeof val === "number" && Number.isFinite(val));
    if (validValues.length === 0) return 0;
    return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  }, []);

  const chartData = useMemo(() => {
    if (datasets?.length && labels?.length) {
      return {
        labels,
        datasets: datasets.map((dataset) => ({
          ...DATASET_DEFAULTS,
          ...dataset,
        })),
      };
    }

    if (data?.length && labelKey && valueKey) {
      const valueKeyLower = String(valueKey).toLowerCase();
      const isMinuteBased = valueKeyLower.includes("minute");

      const hasDurationStrings =
        typeof durationKey === "string" &&
        durationKey.length > 0 &&
        data.some((entry) => parseDurationStringToMinutes(entry?.[durationKey]) > 0);

      const shouldConvertToHours = isMinuteBased || hasDurationStrings;

      if (shouldConvertToHours) {
        const processedEntries = data
          .map((item) => {
            const numericMinutes = parseNumericValue(item?.[valueKey]);
            const hasPositiveMinutes = Number.isFinite(numericMinutes) && numericMinutes > 0;
            const durationMinutes = hasPositiveMinutes
              ? numericMinutes
              : parseDurationStringToMinutes(item?.[durationKey]);

            return {
              item,
              minutesValue: durationMinutes,
            };
          })
          .filter(({ minutesValue }) => minutesValue > 0);

        if (processedEntries.length === 0) {
          return null;
        }

        const derivedLabels = processedEntries.map(({ item }) => String(item?.[labelKey] ?? ""));
        const hoursData = processedEntries.map(({ minutesValue }) =>
          convertMinutesToHours(minutesValue)
        );
        const rawMinutes = processedEntries.map(({ minutesValue }) => minutesValue);
        const colorSourceItems = processedEntries.map(({ item }) => item);
        const durationLabels = processedEntries.map(({ item, minutesValue }) => {
          if (durationKey && typeof item?.[durationKey] === "string" && item[durationKey].trim()) {
            return item[durationKey].trim();
          }

          return formatMinutesToDuration(minutesValue);
        });

        const derivedDataset = {
          ...DATASET_DEFAULTS,
          label: datasetLabel || HOURS_AXIS_LABEL,
          data: hoursData,
          rawMinutes,
          durationLabels,
          ...(datasetOptions || {}),
        };

        if (!derivedDataset.backgroundColor) {
          derivedDataset.backgroundColor = getBarColors(colorSourceItems);
        }

        const chartDatasets = [derivedDataset];

        if (showAverageLine) {
          const average = calculateAverage(hoursData);
          const averageData = new Array(hoursData.length).fill(Number(average.toFixed(2)));

          chartDatasets.push({
            type: "line",
            label: averageLineLabel || "Average",
            data: averageData,
            borderColor: averageLineColor || "#FF6B6B",
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            borderDash: [5, 5],
            tension: 0,
          });
        }

        return {
          labels: derivedLabels,
          datasets: chartDatasets,
        };
      }

      const processedEntries = data
        .map((item) => ({
          label: String(item?.[labelKey] ?? ""),
          value: parseNumericValue(item?.[valueKey]),
        }))
        .filter(({ value }) => value > 0);

      if (processedEntries.length === 0) {
        return null;
      }

      const derivedLabels = processedEntries.map(({ label }) => label);
      const derivedValues = processedEntries.map(({ value }) => value);

      const derivedDataset = {
        ...DATASET_DEFAULTS,
        label: datasetLabel || valueKey,
        data: derivedValues,
        ...(datasetOptions || {}),
      };

      if (!derivedDataset.backgroundColor) {
        derivedDataset.backgroundColor = getBarColors(data);
      }

      const chartDatasets = [derivedDataset];

      if (showAverageLine) {
        const average = calculateAverage(derivedValues);
        const averageData = new Array(derivedValues.length).fill(average);

        chartDatasets.push({
          type: "line",
          label: averageLineLabel || "Average",
          data: averageData,
          borderColor: averageLineColor || "#FF6B6B",
          backgroundColor: "transparent",
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderDash: [5, 5],
          tension: 0,
        });
      }

      return {
        labels: derivedLabels,
        datasets: chartDatasets,
      };
    }

    return null;
  }, [
    datasets,
    labels,
    data,
    labelKey,
    valueKey,
    durationKey,
    datasetLabel,
    datasetOptions,
    parseNumericValue,
    getBarColors,
    showAverageLine,
    averageLineColor,
    averageLineLabel,
    calculateAverage,
  ]);

  const shouldFormatAsHours = useMemo(
    () =>
      chartData?.datasets?.some(
        (dataset) =>
          Array.isArray(dataset.rawMinutes) && dataset.rawMinutes.length === dataset.data.length
      ) ?? false,
    [chartData]
  );

  const shouldHideLegend = useMemo(() => {
    if (!displayLegend) return false;
    if (!chartData?.datasets || chartData.datasets.length !== 1) return false;

    return chartData.datasets[0]?.label === HOURS_AXIS_LABEL;
  }, [chartData, displayLegend]);

  const durationLabelPlugin = useMemo(() => {
    if (!shouldFormatAsHours || !chartData?.datasets?.length) {
      return null;
    }

    return {
      id: "duration-label-plugin",
      afterDatasetsDraw: (chart) => {
        const { ctx } = chart;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (meta.type !== "bar") return;

          meta.data.forEach((element, index) => {
            const label = dataset.durationLabels?.[index];
            if (!label) return;

            const { x, y } = element.tooltipPosition();

            ctx.save();
            ctx.fillStyle = "#111";
            ctx.font = ChartJS.defaults.font?.string || "12px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.fillText(label, x, y - 6);
            ctx.restore();
          });
        });
      },
    };
  }, [chartData, shouldFormatAsHours]);

  const baseOptions = useMemo(
    () => ({
      ...BASE_CHART_OPTIONS,
      maintainAspectRatio,
      plugins: {
        ...BASE_CHART_OPTIONS.plugins,
        legend: {
          display: displayLegend && !shouldHideLegend,
          position: "top",
          labels: {
            usePointStyle: true,
            boxWidth: 12,
          },
        },
        tooltip: {
          ...BASE_CHART_OPTIONS.plugins.tooltip,
          ...(shouldFormatAsHours && {
            callbacks: {
              label: (context) => {
                const value = context.parsed?.y ?? context.parsed;
                if (!Number.isFinite(value)) {
                  return context.dataset.label || "";
                }

                return undefined;
              },
              afterLabel: (context) => {
                const { dataset, dataIndex } = context;

                const durationLabel = dataset.durationLabels?.[dataIndex];
                if (durationLabel) {
                  return `Duration: ${durationLabel}`;
                }

                const minutes = dataset.rawMinutes?.[dataIndex];
                if (Number.isFinite(minutes)) {
                  return `Duration: ${formatMinutesToDuration(minutes)}`;
                }

                return undefined;
              },
            },
          }),
        },
      },
      scales: {
        x: {
          stacked: isStacked,
          grid: {
            display: showGrid,
            drawBorder: false,
          },
        },
        y: {
          stacked: isStacked,
          beginAtZero: yBeginAtZero,
          grid: {
            display: showGrid,
            drawBorder: false,
          },
          ticks: {
            precision: shouldFormatAsHours ? 2 : 0,
            ...(shouldFormatAsHours && {
              callback: (value) => Number(value).toFixed(2),
            }),
          },
          ...(shouldFormatAsHours && {
            title: {
              display: true,
              text: HOURS_AXIS_LABEL,
            },
          }),
        },
      },
    }),
    [
      displayLegend,
      shouldHideLegend,
      showGrid,
      yBeginAtZero,
      maintainAspectRatio,
      isStacked,
      shouldFormatAsHours,
    ]
  );

  const mergedOptions = useMemo(() => {
    if (!options) return baseOptions;

    return {
      ...baseOptions,
      ...options,
      plugins: {
        ...baseOptions.plugins,
        ...(options.plugins || {}),
      },
      scales: {
        ...baseOptions.scales,
        ...(options.scales || {}),
        ...(options.scales?.x && {
          x: {
            ...baseOptions.scales?.x,
            ...options.scales.x,
          },
        }),
        ...(options.scales?.y && {
          y: {
            ...baseOptions.scales?.y,
            ...options.scales.y,
          },
        }),
      },
    };
  }, [baseOptions, options]);

  const hasHeaderContent = title || description;

  if (!chartData) {
    return (
      <MDBox>
        {hasHeaderContent && (
          <MDBox mb={1.5} ml={1}>
            {title && (
              <MDTypography variant="h6" fontWeight="medium">
                {title}
              </MDTypography>
            )}
            {description && (
              <MDTypography variant="button" color="text">
                {description}
              </MDTypography>
            )}
          </MDBox>
        )}
        <MDBox
          sx={{
            position: "relative",
            minHeight: pxToRem(height),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MDTypography variant="button" color="text">
            {loading === Constants.PENDING ? (
              <MDBox py={5} display="flex" justifyContent="center" alignItems="center">
                <Box
                  component="img"
                  src={bgIm}
                  alt="Wind Turbine"
                  sx={{
                    objectFit: "cover",
                  }}
                />
              </MDBox>
            ) : (
              "No Data Available"
            )}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  }

  return (
    <MDBox>
      {hasHeaderContent && (
        <MDBox mb={1.5} ml={1}>
          {title && (
            <MDTypography variant="h6" fontWeight="medium">
              {title}
            </MDTypography>
          )}
          {description && (
            <MDTypography variant="button" color="text">
              {description}
            </MDTypography>
          )}
        </MDBox>
      )}
      <MDBox
        sx={{
          position: "relative",
          minHeight: pxToRem(height),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Bar
          data={chartData}
          options={mergedOptions}
          height={height}
          plugins={durationLabelPlugin ? [durationLabelPlugin] : undefined}
        />
      </MDBox>
    </MDBox>
  );
}

BarChart.defaultProps = {
  labels: [],
  datasets: [],
  options: null,
  title: "",
  description: "",
  height: 320,
  displayLegend: true,
  showGrid: true,
  yBeginAtZero: true,
  maintainAspectRatio: false,
  isStacked: false,
  data: [],
  labelKey: "scope",
  valueKey: "totalMinutes",
  durationKey: "duration",
  datasetLabel: "",
  datasetOptions: null,
  colorCodeKey: "colorCode",
  showAverageLine: false,
  averageLineColor: "#FF6B6B",
  averageLineLabel: "Average",
  loading: Constants.PENDING,
};

const stringOrNumber = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);
const stringOrStringArray = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.arrayOf(PropTypes.string),
]);
const numberOrNumberArray = PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.arrayOf(PropTypes.number),
]);
const shapeOrNull = PropTypes.oneOfType([PropTypes.shape({}), PropTypes.oneOf([null])]);

BarChart.propTypes = {
  labels: PropTypes.arrayOf(stringOrNumber),
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
      backgroundColor: stringOrStringArray,
      borderColor: stringOrStringArray,
      borderWidth: numberOrNumberArray,
      borderRadius: numberOrNumberArray,
      maxBarThickness: PropTypes.number,
    })
  ),
  options: shapeOrNull,
  title: PropTypes.string,
  description: PropTypes.string,
  height: PropTypes.number,
  displayLegend: PropTypes.bool,
  showGrid: PropTypes.bool,
  yBeginAtZero: PropTypes.bool,
  maintainAspectRatio: PropTypes.bool,
  isStacked: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object),
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  durationKey: PropTypes.string,
  showAverageLine: PropTypes.bool,
  datasetLabel: PropTypes.string,
  datasetOptions: shapeOrNull,
  colorCodeKey: PropTypes.string,
  averageLineColor: PropTypes.string,
  averageLineLabel: PropTypes.string,
  loading: PropTypes.string,
};

export default BarChart;
